const DIALOG_FIELD_SELECTOR = [
  'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="checkbox"]):not([type="radio"]):not([type="file"])',
  'textarea',
  'select',
  '[contenteditable="true"]',
  '[contenteditable=""]',
  '[role="combobox"]',
].join(',');

const DIALOG_FALLBACK_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const AUTO_FOCUS_WAIT_MS = 8000;

function isElementHidden(element: HTMLElement): boolean {
  if (element.hidden || element.closest('[hidden]')) {
    return true;
  }
  if (element.classList.contains('hidden') || element.closest('.hidden')) {
    return true;
  }
  if (element.getAttribute('aria-hidden') === 'true' || element.closest('[aria-hidden="true"]')) {
    return true;
  }
  return false;
}

function isEligibleDialogAutoFocusElement(element: HTMLElement): boolean {
  if (element.closest('[data-dialog-close]')) {
    return false;
  }
  if (element.closest('[data-formatting-bar]')) {
    return false;
  }
  if (element.closest('[data-dialog-skip-autofocus]')) {
    return false;
  }
  if ('disabled' in element && (element as HTMLInputElement).disabled) {
    return false;
  }
  if (element.getAttribute('aria-disabled') === 'true') {
    return false;
  }
  if (isElementHidden(element)) {
    return false;
  }
  return true;
}

function firstMatching(
  container: HTMLElement,
  selector: string,
): HTMLElement | null {
  const matches = container.querySelectorAll<HTMLElement>(selector);
  for (const match of matches) {
    if (isEligibleDialogAutoFocusElement(match)) {
      return match;
    }
  }
  return null;
}

export function findFirstDialogField(container: HTMLElement): HTMLElement | null {
  const explicit = firstMatching(container, '[data-dialog-autofocus]');
  if (explicit) {
    return explicit;
  }
  return firstMatching(container, DIALOG_FIELD_SELECTOR);
}

export function findFirstDialogAutoFocusTarget(container: HTMLElement): HTMLElement | null {
  const field = findFirstDialogField(container);
  if (field) {
    return field;
  }

  return firstMatching(container, DIALOG_FALLBACK_FOCUSABLE_SELECTOR);
}

export function focusFirstDialogField(container: HTMLElement): () => void {
  let cancelled = false;
  let observer: MutationObserver | null = null;
  let timeoutId: number | null = null;
  let focusedFallback = false;

  const cleanup = (): void => {
    cancelled = true;
    observer?.disconnect();
    observer = null;
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const tryFocus = (): boolean => {
    if (cancelled) {
      return true;
    }
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      container.contains(active) &&
      active.closest('[data-dialog-skip-autofocus]')
    ) {
      cleanup();
      return true;
    }
    const field = findFirstDialogField(container);
    if (field) {
      if (active !== field) {
        field.focus();
      }
      cleanup();
      return true;
    }
    if (!focusedFallback) {
      const fallback = firstMatching(container, DIALOG_FALLBACK_FOCUSABLE_SELECTOR);
      if (fallback) {
        fallback.focus();
        focusedFallback = true;
      }
    }
    return false;
  };

  if (tryFocus()) {
    return cleanup;
  }

  observer = new MutationObserver(() => {
    tryFocus();
  });
  observer.observe(container, { childList: true, subtree: true, attributes: true });

  timeoutId = window.setTimeout(() => {
    if (!tryFocus() && !cancelled) {
      if (!focusedFallback) {
        container.focus();
      }
    }
    cleanup();
  }, AUTO_FOCUS_WAIT_MS);

  return cleanup;
}
